// The optional configuration that follows a successful install.
//
// WHY IT IS HERE RATHER THAN LEFT TO THE ADMIN PANEL
//
// A fresh instance is installed but not usable: no payment provider, no
// email provider, and no decision about whether competitions run on this
// site or on a partner's. Every one of those is a thing the person doing
// the install already knows and has to hand. Making them find three
// separate admin screens afterwards is how an instance ends up half
// configured for a week.
//
// SAVED PER STEP, NOT AT THE END
//
// Two reasons, and the second is decisive:
//
//   1. Somebody who gets halfway and closes the browser keeps what they
//      entered.
//   2. The "test this now" button cannot test credentials that only exist
//      in a form. They have to be saved before they can be verified,
//      which settles the question.
//
// EVERY STEP IS SKIPPABLE, and the whole thing can be left at any point —
// the mandatory install (root account and 2FA) is already complete by the
// time this renders, so nothing here can lock anybody out. That is also
// why it sits after 2FA rather than before: these steps need an
// authenticated session to save anything.
import React, { useState, useEffect } from "react";
import TestConfigurationBox from "./TestConfigurationBox.jsx";

// ---- THE CONTENT STEPS, AND WHY THEY ARE HERE AT ALL (1.920.0) ----
//
// THE OWNER'S REASON FIRST, because it is the better one: "the user that's
// performing the install knows what data is required and they can enter it as
// they see fit". A site whose terms page still says MOCKUP DOCUMENT a month
// later is one where nobody was ever shown that the page existed.
//
// AND A MECHANICAL ONE UNDERNEATH IT. These four collections are seeded into
// their FILES before the backend connects to MongoDB, so a fresh install
// leaves all four EMPTY in the database - measured on srv1924928, 22 Oct 2026.
// Three of the four survive that, because their read paths normalise and supply
// the shipped defaults from code. contentPages does not: its apply has no
// defaults, so once a partner's reads move to the database an empty collection
// serves no pages at all.
//
// WHICH IS WHY EVERY ONE OF THESE STEPS SAVES EVEN WHEN SKIPPED, through
// admin.install.useDefaultsFor. A step that wrote only when somebody edited
// something would ship that wipe on every install where the installer clicked
// past it - and clicking past things is what an install wizard is for.
const STEPS = ["usage", "payments", "email", "emailContent", "content", "legal", "theme", "done"];

// The sections admin.install.useDefaultsFor accepts, and the order they appear
// in. Named here rather than derived from STEPS, because "usage" and "payments"
// are not content sections and a skipped payments step must not record one.
const CONTENT_SECTIONS = { emailContent: "emailContent", content: "contentPages", legal: "legalDocs", theme: "themes" };

// Midnight Blue, PRESELECTED AND NOT ACTIVATED - the owner's decision, 22 Oct.
//
// activeThemeId null is the shipped default and means "no site-wide override:
// every visitor's own light/dark choice applies". Any other id becomes the one
// theme every visitor sees regardless of their own preference. So this is shown
// selected and takes one confirmation, and SKIPPING LEAVES PER-VISITOR CHOICE
// rather than quietly activating it. That is the one step where skipping does
// not give you the thing on screen, which is why the step says so in words.
const PRESELECTED_THEME = "curated-midnight-blue";

export default function InstallConfigureSteps({ token, wsRequest, onFinish }) {
  const [step, setStep] = useState("usage");
  // INLINE, not a toast — deliberately, and unlike every other panel.
  //
  // This is a linear wizard: an error means "this step did not work, try
  // again", and it belongs beside the step it refers to. A toast that
  // fades after a few seconds is the wrong shape for that, especially
  // during an install somebody does once.
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState({});

  // usage
  const [usage, setUsage] = useState(null); // "native" | "api"

  // payments
  const [payProvider, setPayProvider] = useState("");
  const [cfConfigId, setCfConfigId] = useState("");
  const [cfKey, setCfKey] = useState("");
  const [payCredsSaved, setPayCredsSaved] = useState(false);

  // email
  const [emailProvider, setEmailProvider] = useState("");
  const [emailKey, setEmailKey] = useState("");
  const [emailFrom, setEmailFrom] = useState("");

  // ---- the content steps ----
  //
  // LOADED WHEN THE STEP IS REACHED, not all at mount: three requests before
  // anybody has left the first screen is three ways for the install to show an
  // error about something the installer has not got to yet.
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(null);
  const [docs, setDocs] = useState(null);
  const [boxes, setBoxes] = useState(null);
  const [themes, setThemes] = useState(null);
  const [theme, setTheme] = useState(PRESELECTED_THEME);
  // Keyed by slug / doc key / box key, holding only what has been EDITED. An
  // empty object means nothing was touched, which is what the legal step's
  // acknowledgement turns on.
  const [edits, setEdits] = useState({});
  const [ack, setAck] = useState(false);

  const edit = (key, value) => setEdits((e) => ({ ...e, [key]: value }));
  const valueFor = (key, original) => (key in edits ? edits[key] : original || "");
  const anyEdited = (items) => items.some(([key, original]) => key in edits && edits[key] !== (original || ""));

  useEffect(() => {
    const load = async (fn) => {
      setLoading(true);
      setError(null);
      try {
        await fn();
      } catch (err) {
        setError(err.message || "Could not load this step.");
      } finally {
        setLoading(false);
      }
    };
    if (step === "content" && pages === null) {
      load(async () => setPages(await wsRequest("admin.content.list", {}, token)));
    } else if (step === "legal" && docs === null) {
      load(async () => setDocs(await wsRequest("legalDocs.list", {}, token)));
    } else if (step === "emailContent" && boxes === null) {
      load(async () => setBoxes(await wsRequest("admin.emailContent.get", {}, token)));
    } else if (step === "theme" && themes === null) {
      load(async () => {
        const loaded = await wsRequest("admin.themes.list", {}, token);
        setThemes(loaded);
        // ---- THE SUGGESTED THEME MAY NOT BE THERE ----
        //
        // curated presets are seeded once and then stored like any other
        // custom theme, so root can delete one - and normaliseThemesConfig
        // deliberately never re-adds a preset that was seeded and removed,
        // because removal means it was removed on purpose. A radio bound to an
        // id the list does not contain shows nothing selected while the state
        // still holds it, and "Use this for everyone" would then send an id the
        // server answers 404 for.
        const ids = (loaded?.themes || []).map((t) => t.id);
        if (!ids.includes(PRESELECTED_THEME)) setTheme(ids.length ? ids[0] : "");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ---- SKIPPING A CONTENT STEP STILL WRITES ----
  //
  // It persists the section through its own save path, which reconciles the
  // whole collection, and records that the shipped defaults were kept so the
  // admin panel can say so later. The plain `skip` below stays for the three
  // steps that are configuration rather than content - a skipped payment
  // provider has nothing to seed and nothing to record.
  const keepDefaults = (stepName) =>
    run(
      () => wsRequest("admin.install.useDefaultsFor", { section: CONTENT_SECTIONS[stepName] }, token),
      stepName
    );

  const keepDefaultsButton = (stepName, label = "Use the defaults") => (
    <button type="button" onClick={() => keepDefaults(stepName)} disabled={busy || loading} style={{ opacity: 0.85 }}>
      {busy ? "Saving…" : label}
    </button>
  );

  const next = () => setStep(STEPS[Math.min(STEPS.indexOf(step) + 1, STEPS.length - 1)]);

  const run = async (fn, label) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setSaved((s) => ({ ...s, [label]: true }));
      next();
    } catch (err) {
      setError(err.message || "Could not reach backend.");
    } finally {
      setBusy(false);
    }
  };

  const card = {
    border: "1px solid var(--border-color)",
    borderRadius: 10,
    padding: 18,
    maxWidth: 560,
  };

  const skip = (
    <button type="button" onClick={next} disabled={busy} style={{ opacity: 0.8 }}>
      Skip this
    </button>
  );

  return (
    <div style={{ marginTop: 20 }}>
      <p style={{ fontSize: 13, opacity: 0.75 }}>
        Your site is installed and you can sign in whenever you like. These steps set it up so
        it is ready to use — each one saves as you go, and any of them can be skipped and done
        later from the admin panel.
      </p>

      <div style={{ minHeight: 20, marginBottom: 8 }}>
        {error && <span style={{ color: "var(--accent-red)" }}>{error}</span>}
      </div>

      {step === "usage" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>How will competitions be shown?</h3>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            This can be changed at any time.
          </p>

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
            <input
              type="radio"
              checked={usage === "native"}
              onChange={() => setUsage("native")}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>On this site</strong>
              <br />
              <span style={{ fontSize: 13, opacity: 0.75 }}>
                Buyers come here, see the competition and pay here.
              </span>
            </span>
          </label>

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <input
              type="radio"
              checked={usage === "api"}
              onChange={() => setUsage("api")}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>On another site, through the API</strong>
              <br />
              <span style={{ fontSize: 13, opacity: 0.75 }}>
                Competitions appear on a partner's own site via the widget. This site's
                homepage stops showing them publicly, and signing up here is closed —
                administration still works normally.
              </span>
            </span>
          </label>

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {skip}
            <button
              type="button"
              disabled={busy || usage === null}
              onClick={() =>
                run(
                  () =>
                    // admin.api.updateSettings — verified against the
                    // server rather than assumed. Only publicSiteDisabled
                    // is sent; the action leaves anything omitted alone.
                    wsRequest(
                      "admin.api.updateSettings",
                      { publicSiteDisabled: usage === "api" },
                      token
                    ),
                  "usage"
                )
              }
            >
              {busy ? "Saving…" : "Save and continue"}
            </button>
          </div>
        </div>
      )}

      {step === "payments" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Payment provider</h3>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            Test credentials only for now — live ones are entered later, from the admin panel,
            and the site will not take real payments until you switch it on there.
          </p>

          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Provider</label>
          <select
            value={payProvider}
            onChange={(e) => setPayProvider(e.target.value)}
            disabled={busy}
            style={{ marginBottom: 12 }}
          >
            <option value="">Choose…</option>
            <option value="cashflows">Cashflows</option>
            <option value="paytriot">Paytriot (adapter not yet written)</option>
          </select>

          {payProvider === "paytriot" && (
            <p style={{ fontSize: 13, color: "var(--accent-yellow)" }}>
              Paytriot can be selected and its credentials stored, but the integration itself is
              not built yet — it cannot take a payment. Cashflows is the one that works today.
            </p>
          )}

          {payProvider === "cashflows" && (
            <>
              <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>
                Configuration ID (test)
              </label>
              <input
                type="text"
                value={cfConfigId}
                onChange={(e) => setCfConfigId(e.target.value)}
                disabled={busy}
                style={{ width: "100%", marginBottom: 8 }}
              />
              <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>
                API key (test)
              </label>
              <input
                type="password"
                value={cfKey}
                onChange={(e) => setCfKey(e.target.value)}
                disabled={busy}
                style={{ width: "100%", marginBottom: 8 }}
              />

              {/* Only once they are SAVED — the test reaches the provider
                  with stored credentials, and cannot see a form. */}
              {payCredsSaved && (
                <TestConfigurationBox
                  token={token}
                  wsRequest={wsRequest}
                  provider="cashflows"
                  mode="test"
                  hasCredentials
                />
              )}
            </>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {skip}
            {payProvider === "cashflows" && !payCredsSaved && (
              <button
                type="button"
                disabled={busy || !cfConfigId.trim() || !cfKey.trim()}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await wsRequest(
                      "admin.paymentProvider.setCashflowsCredentials",
                      { mode: "test", configurationId: cfConfigId.trim(), apiKey: cfKey.trim() },
                      token
                    );
                    await wsRequest("admin.paymentProvider.setActive", { active: "cashflows" }, token);
                    setPayCredsSaved(true);
                  } catch (err) {
                    setError(err.message || "Could not reach backend.");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? "Saving…" : "Save credentials"}
              </button>
            )}
            {payProvider === "paytriot" && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  run(
                    () => wsRequest("admin.paymentProvider.setActive", { active: "paytriot" }, token),
                    "payments"
                  )
                }
              >
                Select Paytriot and continue
              </button>
            )}
            {payCredsSaved && (
              <button type="button" onClick={next} disabled={busy}>
                Continue
              </button>
            )}
          </div>
        </div>
      )}

      {step === "email" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Email provider</h3>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            Used for password resets, competition reminders and winner notifications. Without
            one, nobody can reset a forgotten password.
          </p>

          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Provider</label>
          <select
            value={emailProvider}
            onChange={(e) => setEmailProvider(e.target.value)}
            disabled={busy}
            style={{ marginBottom: 12 }}
          >
            <option value="">Choose…</option>
            <option value="resend">Resend</option>
            <option value="brevo">Brevo</option>
            <option value="postmark">Postmark</option>
          </select>

          {emailProvider && (
            <>
              <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>API key</label>
              <input
                type="password"
                value={emailKey}
                onChange={(e) => setEmailKey(e.target.value)}
                disabled={busy}
                style={{ width: "100%", marginBottom: 8 }}
              />
              <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>
                From address
              </label>
              <input
                type="email"
                value={emailFrom}
                onChange={(e) => setEmailFrom(e.target.value)}
                placeholder="competitions@yourdomain.com"
                disabled={busy}
                style={{ width: "100%" }}
              />
            </>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {skip}
            {emailProvider && (
              <button
                type="button"
                disabled={busy || !emailKey.trim() || !emailFrom.trim()}
                onClick={() =>
                  run(async () => {
                    const action = {
                      resend: "admin.emailProvider.setResendCredentials",
                      brevo: "admin.emailProvider.setBrevoCredentials",
                      postmark: "admin.emailProvider.setPostmarkCredentials",
                    }[emailProvider];
                    // fromAddress, not fromEmail — checked against the
                    // action rather than guessed.
                    await wsRequest(
                      action,
                      { apiKey: emailKey.trim(), fromAddress: emailFrom.trim() },
                      token
                    );
                    await wsRequest("admin.emailProvider.setActive", { active: emailProvider }, token);
                  }, "email")
                }
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            )}
          </div>
        </div>
      )}

      {step === "emailContent" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Email wording</h3>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            Every email this site sends already has wording written for it. You can reword any
            of them later under Email — there are too many to go through here, and the shipped
            text is complete rather than placeholder.
          </p>
          {loading && <p style={{ fontSize: 13 }}>Loading…</p>}
          {boxes && (
            <ul style={{ fontSize: 13, columns: 2, paddingLeft: 18, margin: "0 0 12px" }}>
              {Object.entries(boxes.boxes || {}).map(([key, box]) => (
                <li key={key}>{box.label}</li>
              ))}
            </ul>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {keepDefaultsButton("emailContent", "Keep this wording")}
          </div>
        </div>
      )}

      {step === "content" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Your information pages</h3>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            Two pages are written and ready. Neither is linked from anywhere yet — tick a box
            below to put one in your header or footer, or nobody will find them.
          </p>
          {loading && <p style={{ fontSize: 13 }}>Loading…</p>}
          {pages && pages.map((page) => (
            <div key={page.slug} style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>
                Title <span style={{ opacity: 0.6 }}>(/page/{page.slug})</span>
              </label>
              <input
                type="text"
                value={valueFor(`${page.slug}:title`, page.title)}
                onChange={(e) => edit(`${page.slug}:title`, e.target.value)}
                disabled={busy}
                style={{ width: "100%", marginBottom: 6 }}
              />
              <textarea
                value={valueFor(`${page.slug}:html`, page.html)}
                onChange={(e) => edit(`${page.slug}:html`, e.target.value)}
                disabled={busy}
                rows={6}
                style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
              />
              <div style={{ display: "flex", gap: 14, fontSize: 12.5, marginTop: 4 }}>
                <label style={{ display: "flex", gap: 5 }}>
                  <input
                    type="checkbox"
                    checked={valueFor(`${page.slug}:inHeader`, page.inHeader) === true}
                    onChange={(e) => edit(`${page.slug}:inHeader`, e.target.checked)}
                    disabled={busy}
                  />
                  Show in header
                </label>
                <label style={{ display: "flex", gap: 5 }}>
                  <input
                    type="checkbox"
                    checked={valueFor(`${page.slug}:inFooter`, page.inFooter) === true}
                    onChange={(e) => edit(`${page.slug}:inFooter`, e.target.checked)}
                    disabled={busy}
                  />
                  Show in footer
                </label>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {keepDefaultsButton("content", "Leave both as they are")}
            {pages && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    // ---- WHAT CHANGED, WORKED OUT ONCE ----
                    //
                    // The first version of this computed it twice: once to
                    // build each page's payload and again to decide whether
                    // anything had been touched at all. Two expressions of one
                    // rule is how they come to disagree, and here the
                    // disagreement would be silent - a page saved while the
                    // defaults were also recorded as kept, or neither.
                    const changed = pages.map((page) => {
                      const fields = {};
                      for (const f of ["title", "html", "inHeader", "inFooter"]) {
                        const k = `${page.slug}:${f}`;
                        if (k in edits && edits[k] !== page[f]) fields[f] = edits[k];
                      }
                      return { page, fields };
                    }).filter(({ fields }) => Object.keys(fields).length > 0);

                    // ONE CALL PER EDITED PAGE. Each goes through
                    // admin.content.update, which persists the whole
                    // collection - so saving one page closes the shadow gap for
                    // both of them.
                    for (const { page, fields } of changed) {
                      await wsRequest("admin.content.update", { slug: page.slug, ...fields }, token);
                    }
                    // NOTHING EDITED IS STILL A SAVE. Continuing without having
                    // changed anything means the defaults were accepted, and
                    // they have to be written either way - see
                    // admin.install.useDefaultsFor for why.
                    if (changed.length === 0) {
                      await wsRequest("admin.install.useDefaultsFor", { section: "contentPages" }, token);
                    }
                  }, "content")
                }
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            )}
          </div>
        </div>
      )}

      {step === "legal" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Terms, disclaimers and privacy</h3>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            These three ship as <strong>mockups</strong> — placeholder wording written for
            testing. They are not legal advice and must be reviewed by a lawyer for your
            business and jurisdiction before this site takes real money.
          </p>
          {loading && <p style={{ fontSize: 13 }}>Loading…</p>}
          {docs && docs.map((doc) => (
            <div key={doc.key} style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>
                {doc.label} <span style={{ opacity: 0.6 }}>(/legal/{doc.key})</span>
              </label>
              <textarea
                value={valueFor(doc.key, doc.html)}
                onChange={(e) => edit(doc.key, e.target.value)}
                disabled={busy}
                rows={6}
                style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
              />
            </div>
          ))}

          {/* ---- THE ACKNOWLEDGEMENT, AND WHY IT IS NOT A PLAIN SKIP ----

               Every other step here can be skipped silently, because the worst
               a skipped payment provider does is leave the site unable to take
               money - which is obvious the first time somebody tries. A
               placeholder terms page is the opposite: it looks like a terms
               page, serves like one, and the site works perfectly with it. So
               this is the one step where taking the default is a decision
               somebody has to make on purpose rather than by clicking past.  */}
          {docs && !anyEdited(docs.map((d) => [d.key, d.html])) && (
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginTop: 4 }}>
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} disabled={busy} style={{ marginTop: 3 }} />
              <span>I understand these are placeholders and must be replaced before going live.</span>
            </label>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {docs && (
              <button
                type="button"
                disabled={busy || (!anyEdited(docs.map((d) => [d.key, d.html])) && !ack)}
                onClick={() =>
                  run(async () => {
                    // ---- A DOCUMENT EMPTIED IS NOT A DOCUMENT UNCHANGED ----
                    //
                    // admin.legalDocs.saveHtml refuses empty html, so the first
                    // version of this skipped any edit that had been blanked -
                    // and with nothing left to write it then recorded the
                    // defaults as KEPT. Somebody who cleared a document on
                    // purpose would have been told it was saved, and the
                    // placeholder would still be serving. Refused here instead,
                    // before anything is written.
                    const blanked = docs
                      .filter((d) => d.key in edits && !String(edits[d.key]).trim())
                      .map((d) => d.label);
                    if (blanked.length) {
                      throw new Error(
                        `${blanked.join(" and ")} cannot be left empty — these pages are served publicly. ` +
                        "Put wording back, or use the placeholder."
                      );
                    }
                    let wrote = false;
                    for (const doc of docs) {
                      if (doc.key in edits && edits[doc.key] !== (doc.html || "")) {
                        await wsRequest("admin.legalDocs.saveHtml", { key: doc.key, html: edits[doc.key] }, token);
                        wrote = true;
                      }
                    }
                    if (!wrote) {
                      await wsRequest("admin.install.useDefaultsFor", { section: "legalDocs" }, token);
                    }
                  }, "legal")
                }
              >
                {busy
                  ? "Saving…"
                  : anyEdited(docs.map((d) => [d.key, d.html]))
                  ? "Save and continue"
                  : "Keep the placeholders and continue"}
              </button>
            )}
          </div>
        </div>
      )}

      {step === "theme" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>How the site looks</h3>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            Picking one here sets it for <strong>everybody</strong> who visits, overriding each
            visitor's own light/dark choice. Skip this and every visitor keeps their own
            preference, which is how the site behaves today.
          </p>
          {loading && <p style={{ fontSize: 13 }}>Loading…</p>}
          {themes && (themes.themes || []).map((t) => (
            <label key={t.id} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
              <input type="radio" checked={theme === t.id} onChange={() => setTheme(t.id)} disabled={busy} />
              <span style={{ fontSize: 13 }}>
                {t.name}
                {t.id === PRESELECTED_THEME && <span style={{ opacity: 0.6 }}> — suggested</span>}
              </span>
            </label>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {/* SKIPPING HERE DOES NOT GIVE YOU THE SELECTED THEME, which is why
                the button says what it does rather than "Skip this". It saves -
                leaving activeThemeId null - and records that the default was
                kept. */}
            {keepDefaultsButton("theme", "Let each visitor choose")}
            {themes && (
              <button
                type="button"
                disabled={busy || !theme}
                onClick={() => run(() => wsRequest("admin.themes.setActive", { id: theme }, token), "theme")}
              >
                {busy ? "Saving…" : "Use this for everyone"}
              </button>
            )}
          </div>
        </div>
      )}

      {step === "done" && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>All set</h3>
          <ul style={{ fontSize: 13, paddingLeft: 18 }}>
            <li>Usage: {saved.usage ? "saved" : "skipped — set it under Partners later"}</li>
            <li>
              Payments:{" "}
              {payCredsSaved || saved.payments
                ? "saved"
                : "skipped — set it under Finance later"}
            </li>
            <li>Email: {saved.email ? "saved" : "skipped — set it under Email later"}</li>
            {/* ---- THE CONTENT SECTIONS READ DIFFERENTLY HERE ----

                 "skipped" is the wrong word for these four: they were SAVED
                 either way, with the shipped defaults kept. The difference is
                 recorded on the site rather than only said here, so somebody
                 who did not run the install can still find out which sections
                 are still carrying placeholder wording.  */}
            <li>Email wording: {saved.emailContent ? "kept the shipped wording" : "not reached"}</li>
            <li>Information pages: {saved.content ? "saved" : "not reached"}</li>
            <li>
              Terms, disclaimers and privacy:{" "}
              {saved.legal ? "saved — replace the placeholders before going live" : "not reached"}
            </li>
            <li>Appearance: {saved.theme ? "saved" : "not reached"}</li>
          </ul>
          <p style={{ fontSize: 13, opacity: 0.8 }}>
            Anything left at its defaults can be changed from the admin panel at any time, and
            the panel will keep saying which sections are still untouched until they are.
          </p>
          <button type="button" onClick={onFinish}>
            Go to sign in
          </button>
        </div>
      )}
    </div>
  );
}
